"use client";

import {
  isServer,
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { CommandBarProvider } from "@/hooks/use-command-bar";
import { CommandBar } from "@/components/command-bar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EntityInstanceModalHost } from "@/components/entity-instance-table/entity-instance-modal-host";
import { UnsavedChangesProvider } from "@/components/navigation/unsaved-changes-guard";
import { makeQueryClient } from "@/lib/query-client";

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

interface ProvidersProps {
  children: React.ReactNode;
  dehydratedState?: DehydratedState;
}

export const Providers = ({ children, dehydratedState }: ProvidersProps) => {
  const queryClient = getQueryClient();
  const isDev = process.env.NODE_ENV === "development";

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <NuqsAdapter>
          <TooltipProvider>
            <CommandBarProvider>
              <UnsavedChangesProvider>
                {children}
                <CommandBar />
                <EntityInstanceModalHost />
                {/* {isDev && <ReactQueryDevtools initialIsOpen={false} />} */}
              </UnsavedChangesProvider>
            </CommandBarProvider>
          </TooltipProvider>
        </NuqsAdapter>
      </HydrationBoundary>
    </QueryClientProvider>
  );
};
